import { useState, useEffect } from "react";
import axiosVortex from "../../../utils/axios.instance";
import type { OptionResponseData } from "../../../models/optionChain";
import { findLTPLessThan10Percent, multiplequoteURLMaker } from "../../../utils/helpers";

// Define the interface for the hook's props
export interface ReducedMarginProps {
  memorizedLTP: number;
  optionType?: "PE" | "CE";
  isChecked: boolean;
  symbolInfo?: string;
  odin_token?: string;
  odin_exchange?: string;
  odin_expiry_date?: string;
}

const useReducedMargin = ({
  memorizedLTP,
  optionType,
  isChecked,
  symbolInfo,
  odin_token,
  odin_exchange,
  odin_expiry_date,
}: ReducedMarginProps) => {
  const [data, setData] = useState<{requitedToken : number,ltp: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Skip fetching if requirements aren't met
    if (!isChecked || 
        !optionType || 
        !symbolInfo || 
        !odin_token || 
        !odin_exchange || 
        !odin_expiry_date) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Define the payload for the option chain API call
        const payload = {
          symbol: symbolInfo,
          token: +odin_token,
          exchange: odin_exchange,
          expiry_date: odin_expiry_date,
        };

        // Fetch option chain data
        const optionResponse = await axiosVortex.post(
          "strategies/option_chain",
          payload
        );

        const optionDataResponse: OptionResponseData = optionResponse.data.response;
        const exchange = optionDataResponse.options?.exchange;
        
        if (!exchange || !optionDataResponse.options?.list) {
          throw new Error("Invalid option chain data");
        }

        // Find current option position in the list
        const currentPointer = optionDataResponse.options.list.findIndex(
          (item) => {
            return optionType === "CE" 
              ? item?.CE?.token === +odin_token 
              : item?.PE?.token === +odin_token;
          }
        );

        if (currentPointer === -1) {
          throw new Error("Current option not found in chain");
        }

        // Get token array based on option type
        let tokenArray: number[] = [];
        if (optionType === "CE") {
          tokenArray = optionDataResponse.options.list
            .slice(currentPointer, currentPointer + 20)
            .map((item) => item.CE?.token)
            .filter(Boolean)
            .map(Number);
        } else {
          // For PE, we need to go backward in the list (higher strikes are earlier in array)
          const startIdx = Math.max(0, currentPointer - 19);
          tokenArray = optionDataResponse.options.list
            .slice(startIdx, currentPointer + 1)
            .reverse()
            .map((item) => item.PE?.token)
            .filter(Boolean)
            .map(Number);
        }

        if (tokenArray.length === 0) {
          throw new Error("No valid options found in the selected range");
        }

        // Fetch LTP data for selected tokens
        const ltpByQuoteResponse = await axiosVortex.get(
          multiplequoteURLMaker(exchange, tokenArray)
        );

        // Find option with LTP less than 10% of memorized LTP
        const { ltpLessThan10Percent,ltpPrice } = findLTPLessThan10Percent({
          memorizedLTP,
          ltps: ltpByQuoteResponse.data.data,
        });

        if (!ltpLessThan10Percent) {
          throw new Error("No suitable option found within 10% price threshold");
        }

        const token = ltpLessThan10Percent.split("-")[1];
        setData({
          requitedToken: Number(token),
          ltp: ltpPrice,
        });
        
      } catch (err) {
        console.error("Error in useReducedMargin:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    isChecked,
    optionType,
    memorizedLTP,
    symbolInfo,
    odin_token,
    odin_exchange,
    odin_expiry_date
  ]);

  return { data, loading, error };
};

export default useReducedMargin;